'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/app/components/button';
import { IconClose } from '@/app/components/icons/ui-actions/icon-close';
import { Modal } from '@/app/components/overlays/modal';
import { PAGE_URL } from '@/app/components/sections/content-hub/contents';
import { BaseWrapper } from '@/app/components/sections/content-hub/modal/base-wrapper';
import { ContentFooter } from '@/app/components/sections/content-hub/modal/content-footer';
import { ContentWrapper } from '@/app/components/sections/content-hub/modal/content-wrapper';
import { MediaResult } from '@/app/components/sections/content-hub/modal/media-result';
import { CONTENT_HUB_DATA_TEST } from '@/app/components/sections/content-hub/tracking';
import type { ContentHubEntry } from '@/app/components/sections/content-hub/types';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

const SOCIAL_HEIGHT = 72;
const HEADER_HEIGHT = 56;

interface ModalContentHubPageProps {
  item: ContentHubEntry;
  category: string;
}

export const ModalContentHubPage = ({
  item,
  category,
}: ModalContentHubPageProps) => {
  const router = useRouter();

  const imageWrapperRef = useRef<HTMLDivElement | null>(null);
  const contentWrapperRef = useRef<HTMLDivElement | null>(null);

  const experienceRef = useRef<HTMLDivElement | null>(null);
  const [isLongImage, setIsLongImage] = useState<boolean>(false);

  const image = item.thumbnailImage;
  const imageWidth = image?.width || 16;
  const imageHeight = image?.height || 9;

  const handleOnClose = () => {
    const searchParams = new URLSearchParams(window.location.search);
    router.push(
      `${PAGE_URL}/${category}${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`
    );
  };

  useEffect(() => {
    const imgEl = imageWrapperRef.current;
    if (!imgEl) return;

    const adjustImagePreview = () => {
      const experienceEl = experienceRef.current;
      const contentAreaHeight = window.innerHeight - HEADER_HEIGHT;

      if (experienceEl) {
        setIsLongImage(
          imgEl.clientHeight + experienceEl.clientHeight > contentAreaHeight
        );
      } else {
        setIsLongImage(imgEl.clientHeight + SOCIAL_HEIGHT > contentAreaHeight);
      }
    };

    adjustImagePreview();

    const contentEl = contentWrapperRef.current;
    if (!contentEl) return;

    const resizeObserver = new ResizeObserver(() => {
      adjustImagePreview();
    });
    resizeObserver.observe(contentEl);

    return () => {
      resizeObserver.disconnect();
      resizeObserver.unobserve(contentEl);
    };
  }, []);

  return (
    <>
      <h1 className="sr-only">{item.meta.ogTitle}</h1>
      <Modal
        isOpen
        autoFocus={false}
        isScrollOutside
        sizes="lg"
        overlayKey="modal-gallery"
        className={cn(
          'flex h-full flex-col items-center',
          'md:h-fit md:max-w-none',
          'xl:max-w-none'
        )}
        scrollOutsideContainerClassName="md:m-10"
        animateTransitionClassName="transition-none"
        onClose={handleOnClose}
      >
        <BaseWrapper>
          <div className="sticky top-0 z-20 flex h-14 w-full justify-end bg-white">
            <Button
              variant="tertiary"
              color="black"
              iconBefore={<IconClose />}
              className="my-2 mr-2 md:size-10"
              aria-label="Close"
              {...createDataAttributes({
                test: `${CONTENT_HUB_DATA_TEST.modal}-close`,
              })}
              onClick={handleOnClose}
            />
          </div>

          <div
            ref={contentWrapperRef}
            className={cn(
              'flex h-full flex-col justify-center pb-14 md:overflow-hidden md:pt-4',
              {
                'justify-start': isLongImage,
              }
            )}
          >
            <ContentWrapper imageWidth={imageWidth} imageHeight={imageHeight}>
              <div className="flex justify-center md:px-10">
                <MediaResult
                  ref={imageWrapperRef}
                  item={item}
                  category={category}
                  isPage
                />
              </div>
            </ContentWrapper>

            <ContentFooter
              experienceRef={experienceRef}
              category={category}
              item={item}
            />
          </div>
        </BaseWrapper>
      </Modal>
    </>
  );
};
